import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  ApplicationRole,
  ManagedUserResponse,
  UserViewModel,
  UsersService,
} from "../../../api";
import UserForm from "../Attorneys/AttorneysDetails/UserForm";
import { isUserActive, toggleUserActive } from "./userActiveActions";
import ResetPasswordDialog from "./ResetPasswordDialog";

const MySwal = withReactContent(Swal);

const ROLE_LABELS: Record<string, string> = {
  [ApplicationRole.ADMIN]: "Admin",
  [ApplicationRole.ATTORNEY]: "Attorney",
  [ApplicationRole.STAFF]: "Staff",
  [ApplicationRole.STAFF_ATTORNEY]: "Staff Attorney",
  [ApplicationRole.USER]: "User",
  [ApplicationRole.REFFERAL_STAFF]: "Referral Staff",
  [ApplicationRole.CLIO_STAFF]: "Clio Staff",
  [ApplicationRole.CLIO_ADMIN]: "Clio Admin",
  [ApplicationRole.CLIO_MANAGER]: "Clio Manager",
  [ApplicationRole.CLIO_TASK_ASSIGNER]: "Clio Task Assigner",
  [ApplicationRole.QA]: "QA",
};

function getInitials(user: UserViewModel) {
  const first = user.firstName?.[0] ?? "";
  const last = user.lastName?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || "?";
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("MM/DD/YYYY") : "—";
}

function isAttorneyUser(user: UserViewModel) {
  return (
    user.roles?.includes(ApplicationRole.ATTORNEY) ||
    user.roles?.includes(ApplicationRole.USER) ||
    false
  );
}

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

  const loadUser = () => {
    if (!id) return;
    setLoading(true);
    UsersService.getApiUsers(+id)
      .then(setUser)
      .catch(() => {
        setUser(null);
        MySwal.fire({
          icon: "error",
          title: <p>Could not load user.</p>,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEditClick = () => {
    if (isEdit && user) {
      UserForm.resetFormWith(user);
    }
    setIsEdit(!isEdit);
  };

  const handleToggleActive = async () => {
    if (!user?.id) return;

    const activating = !isUserActive(user);
    await toggleUserActive(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      isActive: activating,
      onSuccess: () => {
        setUser((prev) => (prev ? { ...prev, isActive: activating } : prev));
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Loading user...</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/staff-users")}>
          Back to users
        </Button>
        <Typography sx={{ mt: 2 }}>User not found.</Typography>
      </Box>
    );
  }

  const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "User";
  const resetPasswordUser: ManagedUserResponse = {
    id: user.id!,
    email: user.email ?? "",
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    isActive: isUserActive(user),
    roles: user.roles,
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/staff-users")}
        sx={{ mb: 2 }}
      >
        Back to users
      </Button>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={user.photo?.url ?? undefined}
              sx={{ width: 64, height: 64, bgcolor: "primary.main", fontSize: 22 }}
            >
              {getInitials(user)}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
              {user.phoneNumber && (
                <Typography variant="body2" color="text.secondary">
                  {user.phoneNumber}
                </Typography>
              )}
              <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: "wrap", gap: 0.75 }}>
                {isUserActive(user) ? (
                  <Chip
                    size="small"
                    label="Active"
                    sx={{
                      bgcolor: "dirtyGreen.main",
                      color: "dirtyGreen.contrastText",
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <Chip size="small" label="Inactive" variant="outlined" />
                )}
                {user.isPending && (
                  <Chip
                    size="small"
                    label="Pending"
                    color="secondary"
                    sx={{
                      color: "secondary.contrastText",
                      fontWeight: 600,
                    }}
                  />
                )}
                {(user.roles ?? []).map((role) => (
                  <Chip
                    key={role}
                    size="small"
                    label={ROLE_LABELS[role] ?? role}
                    color="secondary"
                    sx={{
                      color: "secondary.contrastText",
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.5, sm: 2 }}
                sx={{ mt: 1.5 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Joined: {formatDate(user.createdAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last updated: {formatDate(user.updatedAt)}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {!isEdit ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleEditClick}
              >
                Edit
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleEditClick}
              >
                Cancel edit
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setResetPasswordOpen(true)}
            >
              Reset password
            </Button>
            <Button
              variant="outlined"
              color={isUserActive(user) ? "error" : "primary"}
              onClick={handleToggleActive}
            >
              {isUserActive(user) ? "Deactivate" : "Reactivate"}
            </Button>
            {isAttorneyUser(user) && user.id && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate(`/attorney/${user.id}`)}
              >
                Attorney profile
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Profile
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isEdit
            ? "Update the fields below, then save from the form."
            : "View user information. Click Edit to make changes."}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {user.email && (
          <UserForm
            user={user}
            isEdit={isEdit}
            isAttorney={isAttorneyUser(user)}
            onSave={() => {
              setIsEdit(false);
              loadUser();
            }}
          />
        )}
      </Paper>

      <ResetPasswordDialog
        open={resetPasswordOpen}
        user={resetPasswordUser}
        onClose={() => setResetPasswordOpen(false)}
      />
    </Box>
  );
}
